/**
 * Minimal Supabase (PostgREST) client — deliberately plain fetch() instead
 * of the @supabase/supabase-js SDK, matching this project's existing
 * pattern (see multisetRestClient.ts) of talking directly to documented
 * REST endpoints instead of pulling in a full SDK. The query/insert/
 * update/delete conventions below are PostgREST's own stable, documented
 * REST surface (postgrest.org — what every Supabase project exposes at
 * `{project-url}/rest/v1/{table}`), not invented.
 *
 * Auth model: the anon/publishable key is meant to be public (it's what
 * ships in every Supabase client-side app) and access is gated by the
 * Postgres Row Level Security policies set on each table (see the
 * `create_navme_poi_and_graph_tables` migration) — there is currently no
 * per-user auth, so those policies allow public read/write.
 * TODO(production): once an auth flow exists, tighten the insert/update/
 * delete policies to authenticated staff only (see README "Security notes").
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export function edgeFunctionUrl(name: string): string {
  return `${SUPABASE_URL ?? ''}/functions/v1/${name}`;
}

export function isSupabaseConfigured(): boolean {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
}

// ---------- Staff auth (Supabase Auth, password grant) ----------
// Reads stay public (anon key); WRITES require an authenticated staff user
// (enforced by RLS). Create staff accounts in the Supabase dashboard:
// Authentication → Users → Add user (email + password, auto-confirm).

interface AuthSession {
  access_token: string;
  refresh_token: string;
  /** Epoch seconds. */
  expires_at: number;
  email: string;
}

const AUTH_KEY = 'navme.auth.v1';

function loadAuth(): AuthSession | null {
  try {
    return JSON.parse(localStorage.getItem(AUTH_KEY) ?? 'null') as AuthSession | null;
  } catch {
    return null;
  }
}

let auth: AuthSession | null = loadAuth();

function saveAuth(): void {
  if (auth) localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
  else localStorage.removeItem(AUTH_KEY);
}

export function isAuthed(): boolean {
  return auth !== null;
}

export function authedEmail(): string | null {
  return auth?.email ?? null;
}

export async function signIn(email: string, password: string): Promise<void> {
  assertConfigured();
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: { apikey: SUPABASE_ANON_KEY!, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = (await res.json()) as {
    access_token?: string;
    refresh_token?: string;
    expires_at?: number;
    error_description?: string;
    msg?: string;
  };
  if (!res.ok || !data.access_token) {
    throw new Error(data.error_description ?? data.msg ?? `Sign-in failed (HTTP ${res.status})`);
  }
  auth = {
    access_token: data.access_token,
    refresh_token: data.refresh_token ?? '',
    expires_at: data.expires_at ?? Math.floor(Date.now() / 1000) + 3600,
    email,
  };
  saveAuth();
}

export function signOut(): void {
  auth = null;
  cachedProfile = undefined;
  saveAuth();
}

// ---------- Role (staff vs super admin) ----------

/** The signed-in user's platform role; null when not allowlisted. Cached per session. */
export type StaffRole = 'staff' | 'customer_admin' | 'super';
export interface StaffProfile {
  role: StaffRole;
  customerId: string | null;
}
let cachedProfile: StaffProfile | null | undefined;

/** Role + tenant of the signed-in staffer (null = not allowlisted). */
export async function fetchMyProfile(): Promise<StaffProfile | null> {
  if (!isAuthed()) return null;
  if (cachedProfile !== undefined) return cachedProfile;
  try {
    const rows = await pgSelect<{ role: string; customer_id: string | null }>(
      'staff_allowlist',
      `select=role,customer_id&email=eq.${encodeURIComponent(authedEmail() ?? '')}`,
    );
    cachedProfile = rows[0]
      ? {
          role:
            rows[0].role === 'super'
              ? 'super'
              : rows[0].role === 'customer_admin'
                ? 'customer_admin'
                : 'staff',
          customerId: rows[0].customer_id,
        }
      : null;
  } catch {
    cachedProfile = null;
  }
  return cachedProfile;
}

export async function fetchMyRole(): Promise<StaffRole | null> {
  return (await fetchMyProfile())?.role ?? null;
}

// ---------- Staff auth: Google OAuth (Supabase Auth provider) ----------
// Requires the Google provider to be enabled in the Supabase dashboard
// (Authentication → Providers → Google) and the app origin added under
// Authentication → URL Configuration → Redirect URLs. Access is still
// gated by the staff_allowlist table (is_staff() in RLS policies) — a
// Google sign-in from a non-allowlisted account authenticates but cannot
// write.

/**
 * Starts the Google OAuth flow (full-page redirect). On return, Supabase
 * puts the session tokens in the URL hash; consumeOAuthRedirect() (called
 * at boot) stores them and cleans the URL.
 */
export function signInWithGoogle(): void {
  assertConfigured();
  const redirectTo = `${location.origin}/?admin=1`;
  location.href = `${SUPABASE_URL}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(redirectTo)}`;
}

/**
 * Call once at boot, before any UI reads isAuthed(): if the URL hash
 * contains Supabase OAuth tokens (#access_token=…), store them as the
 * staff session and strip them from the URL/history. Returns true when a
 * session was consumed.
 */
export function consumeOAuthRedirect(): boolean {
  if (!location.hash.includes('access_token=')) return false;
  const params = new URLSearchParams(location.hash.slice(1));
  const accessToken = params.get('access_token');
  if (!accessToken) return false;
  const expiresAt =
    Number(params.get('expires_at')) ||
    Math.floor(Date.now() / 1000) + (Number(params.get('expires_in')) || 3600);
  auth = {
    access_token: accessToken,
    refresh_token: params.get('refresh_token') ?? '',
    expires_at: expiresAt,
    email: emailFromJwt(accessToken) ?? 'Google account',
  };
  saveAuth();
  // Never leave tokens in the URL/history.
  history.replaceState(null, '', location.pathname + location.search);
  return true;
}

/** Display email from the JWT payload (no verification needed — display only; RLS re-reads the email server-side from the verified token). */
function emailFromJwt(jwt: string): string | null {
  try {
    const payload = JSON.parse(
      atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { email?: unknown };
    return typeof payload.email === 'string' ? payload.email : null;
  } catch {
    return null;
  }
}

/** Refreshes the access token when close to expiry. Call before writes. */
async function ensureFreshAuth(): Promise<void> {
  if (!auth) return;
  if (Date.now() / 1000 < auth.expires_at - 60) return;
  try {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { apikey: SUPABASE_ANON_KEY!, 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: auth.refresh_token }),
    });
    const data = (await res.json()) as AuthSession & { access_token?: string };
    if (res.ok && data.access_token) {
      auth = { ...auth, ...data };
      saveAuth();
    } else {
      signOut(); // refresh rejected — force re-login
    }
  } catch {
    /* transient network error — keep old token, next write may still work */
  }
}

function assertConfigured(): void {
  if (!isSupabaseConfigured()) {
    throw new Error(
      'Supabase is not configured — set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (see .env.example).',
    );
  }
}

function pgHeaders(extra?: Record<string, string>): HeadersInit {
  return {
    apikey: SUPABASE_ANON_KEY!,
    // Staff token (RLS 'authenticated' role) when signed in; anon otherwise.
    Authorization: `Bearer ${auth?.access_token ?? SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
    ...extra,
  };
}

/** `query` is a raw PostgREST query string, e.g. "select=*&order=name". */
export async function pgSelect<T>(table: string, query = ''): Promise<T[]> {
  assertConfigured();
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}${query ? `?${query}` : ''}`, {
    headers: pgHeaders(),
  });
  if (!res.ok) throw new Error(await pgErrorMessage(res));
  return (await res.json()) as T[];
}

export async function pgInsert<T>(table: string, row: Record<string, unknown>): Promise<T> {
  assertConfigured();
  await ensureFreshAuth();
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
    method: 'POST',
    headers: pgHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(row),
  });
  if (!res.ok) throw new Error(await pgErrorMessage(res));
  const data = (await res.json()) as T[];
  return data[0];
}

export async function pgUpdate<T>(table: string, id: string, patch: Record<string, unknown>): Promise<T> {
  assertConfigured();
  await ensureFreshAuth();
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: pgHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await pgErrorMessage(res));
  const data = (await res.json()) as T[];
  if (!data[0]) {
    // PostgREST returns 200 + [] when RLS filters the row out — surface it
    // instead of silently "saving" nothing.
    throw new Error(`Update affected 0 rows in ${table} — row missing or blocked by policy`);
  }
  return data[0];
}

/**
 * Uploads a file to a public Storage bucket and returns its public URL.
 * Path collisions overwrite (x-upsert) — callers namespace paths by POI id.
 */
export async function uploadPublicFile(bucket: string, path: string, file: File): Promise<string> {
  assertConfigured();
  await ensureFreshAuth();
  const res = await fetch(`${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY!,
      Authorization: `Bearer ${auth?.access_token ?? SUPABASE_ANON_KEY}`,
      'Content-Type': file.type || 'application/octet-stream',
      'x-upsert': 'true',
    },
    body: file,
  });
  if (!res.ok) throw new Error(await pgErrorMessage(res));
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}`;
}

/** Update by arbitrary PostgREST filter (for tables not keyed on `id`). Returns updated row count. */
export async function pgUpdateWhere(
  table: string,
  filterQuery: string,
  patch: Record<string, unknown>,
): Promise<number> {
  assertConfigured();
  await ensureFreshAuth();
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${filterQuery}`, {
    method: 'PATCH',
    headers: pgHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await pgErrorMessage(res));
  const data = (await res.json()) as unknown[];
  return data.length;
}

/**
 * Upsert with merge-duplicates and NO representation — required for
 * write-only tables (e.g. customer_credentials) where the writer's RLS
 * cannot SELECT the row back.
 */
export async function pgUpsert(
  table: string,
  row: Record<string, unknown>,
  onConflict: string,
): Promise<void> {
  assertConfigured();
  await ensureFreshAuth();
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/${table}?on_conflict=${encodeURIComponent(onConflict)}`,
    {
      method: 'POST',
      headers: pgHeaders({ Prefer: 'resolution=merge-duplicates,return=minimal' }),
      body: JSON.stringify(row),
    },
  );
  if (!res.ok) throw new Error(await pgErrorMessage(res));
}

/** Public URL for an object in a public bucket. */
export function publicFileUrl(bucket: string, path: string): string {
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}`;
}

/** Lists objects in a storage bucket under a prefix (names only). */
export async function listBucket(
  bucket: string,
  prefix: string,
  limit = 200,
): Promise<Array<{ name: string; created_at?: string }>> {
  assertConfigured();
  await ensureFreshAuth();
  const res = await fetch(`${SUPABASE_URL}/storage/v1/object/list/${bucket}`, {
    method: 'POST',
    headers: pgHeaders(),
    body: JSON.stringify({ prefix, limit, sortBy: { column: 'name', order: 'desc' } }),
  });
  if (!res.ok) throw new Error(await pgErrorMessage(res));
  return (await res.json()) as Array<{ name: string; created_at?: string }>;
}

/** Delete by arbitrary PostgREST filter, e.g. "or=(from_node.eq.n1,to_node.eq.n1)". */
export async function pgDeleteWhere(table: string, filterQuery: string): Promise<void> {
  assertConfigured();
  await ensureFreshAuth();
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${filterQuery}`, {
    method: 'DELETE',
    headers: pgHeaders(),
  });
  if (!res.ok) throw new Error(await pgErrorMessage(res));
}

export async function pgDelete(table: string, id: string): Promise<void> {
  assertConfigured();
  await ensureFreshAuth();
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: pgHeaders(),
  });
  if (!res.ok) throw new Error(await pgErrorMessage(res));
}

async function pgErrorMessage(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { message?: string; hint?: string };
    return data.message ?? `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}
